/**
 * AIMDv2 – DocumentRow
 *
 * Renders a single uploaded or previously available document item.
 * Shows: file icon, title, type badge, processing status indicator,
 * instructions, and a toggle/checkbox to select/deselect.
 *
 * Also shows a delete icon for newly uploaded documents.
 */

import React from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DocumentItem } from '../types';

interface DocumentRowProps {
  document: DocumentItem;
  onToggle: (id: string) => void;
  onRemove?: (id: string) => void;
}

const DocumentRow: React.FC<DocumentRowProps> = ({ document, onToggle, onRemove }) => {
  const isProcessing = document.processingStatus === 'processing' || document.processingStatus === 'uploaded';
  const isFailed = document.processingStatus === 'failed';
  // A document can only be selected once it has finished indexing. Unknown
  // (null) status is treated as ready so legacy docs aren't hidden. Unticking
  // is always allowed: uploads arrive pre-selected while still indexing, and
  // the admin must be able to drop one rather than wait on it.
  const selectable = !isProcessing && !isFailed;
  const canToggle = selectable || document.selected;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '20px',
        opacity: isFailed ? 0.5 : isProcessing ? 0.7 : 1
      }}
    >
      {/* File info */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
        <InsertDriveFileOutlinedIcon
          sx={{ fontSize: 24, color: 'var(--Stroke-icon-dim, #868686)', mt: '2px' }}
        />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                fontFamily: 'Inter',
                color: 'var(--Text-text-primary, #FDFDFD)',
                lineHeight: '16px'
              }}
            >
              {document.title}
            </Typography>
            <Box
              sx={{
                px: 1,
                py: '5px',
                background: 'var(--Fill-Button-button-gray, #565656)',
                borderRadius: '20px'
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: 'Inter',
                  color: '#FDFDFD',
                  textTransform: 'uppercase',
                  lineHeight: '12px'
                }}
              >
                {document.fileTypeLabel}
              </Typography>
            </Box>

            {/* Processing indicator */}
            {isProcessing && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <CircularProgress size={14} sx={{ color: 'var(--Stroke-highlight, #F79420)' }} />
                <Typography
                  sx={{ fontSize: 12, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Stroke-highlight, #F79420)' }}
                >
                  Indexing
                </Typography>
              </Box>
            )}
            {isFailed && (
              <ErrorOutlineIcon sx={{ fontSize: 16, color: 'var(--UI-Error, #F44336)' }} />
            )}
          </Box>

          {document.instructions && (
            <Typography
              sx={{
                fontSize: 12,
                fontFamily: 'Inter',
                color: 'var(--Text-text-tertiary, #868686)',
                lineHeight: '16px'
              }}
            >
              Instructions: {document.instructions}
            </Typography>
          )}

          {isProcessing && (
            <Typography
              sx={{
                fontSize: 12,
                fontFamily: 'Inter',
                color: 'var(--Text-text-tertiary, #868686)',
                lineHeight: '16px'
              }}
            >
              Indexing, available to use once ready.
            </Typography>
          )}

          {isFailed && (
            <Typography
              sx={{
                fontSize: 12,
                fontFamily: 'Inter',
                color: 'var(--UI-Error, #F44336)',
                lineHeight: '16px'
              }}
            >
              Processing failed
            </Typography>
          )}
        </Box>
      </Box>

      {/* Selection toggle — can't be ticked until the document has finished
          indexing, but can always be unticked */}
      <Box
        onClick={() => canToggle && onToggle(document.id)}
        sx={{
          cursor: canToggle ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          pt: '2px'
        }}
      >
        {document.selected ? (
          <CheckCircleIcon sx={{ fontSize: 24, color: 'var(--UI-Success, #4CAF50)' }} />
        ) : (
          <RadioButtonUncheckedIcon
            sx={{ fontSize: 24, color: 'var(--Stroke-icon-dim, #868686)', opacity: selectable ? 1 : 0.5 }}
          />
        )}
      </Box>

      {/* Delete (only for new uploads) */}
      {onRemove && document.isNew && (
        <IconButton
          onClick={() => onRemove(document.id)}
          size="small"
          sx={{ color: 'var(--Stroke-icon-dim, #868686)', '&:hover': { color: 'var(--UI-Error, #F44336)' } }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default DocumentRow;
